import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import type { InstructorDiscipline } from '../../api/types/instructor.types';
import type {
  ActionResult,
  ActionType,
  UseInstructorDisciplineActionsParams,
  UseInstructorDisciplineActionsReturn,
} from './useInstructorDisciplineActions.types';

import { usePostInstructorRequest } from '../use-post-instructor-request/usePostInstructorRequest';

export function useInstructorDisciplineActions(
  params: UseInstructorDisciplineActionsParams
): UseInstructorDisciplineActionsReturn {
  const { trainingCenterName, onUploadLetter, customerId } = params;
  const request = usePostInstructorRequest(params);
  const queryClient = useQueryClient();
  const [pendingAuthorizationId, setPendingAuthorizationId] = React.useState<
    string | null
  >(null);
  const [lastActionResult, setLastActionResult] =
    React.useState<ActionResult | null>(null);

  const isAnyPending = request.isPending;

  const clearLastActionResult = React.useCallback((): void => {
    setLastActionResult(null);
  }, []);

  const handleDisciplineAction = React.useCallback(
    (
      discipline: InstructorDiscipline,
      programId: string,
      description?: string
    ): void => {
      const makeCallbacks = (
        action: ActionType
      ): {
        onSuccess: (result: { status: InstructorDiscipline['state'] }) => void;
        onError: () => void;
      } => ({
        onSuccess: (result: { status: InstructorDiscipline['state'] }) => {
          const updatedDiscipline = { ...discipline, state: result.status };
          setLastActionResult({
            status: 'success',
            discipline: updatedDiscipline,
            programId,
            action,
          });
          setPendingAuthorizationId(null);
          void queryClient.invalidateQueries({
            queryKey: ['instructors', customerId],
          });
          void queryClient.invalidateQueries({
            queryKey: ['instructors', 'unapproved', customerId],
          });
          void queryClient.invalidateQueries({
            queryKey: ['instructors', 'pending-letter', customerId],
          });
        },
        onError: () => {
          setPendingAuthorizationId(null);
          setLastActionResult({
            status: 'error',
            discipline,
            programId,
            action,
          });
        },
      });

      switch (discipline.state) {
        case 'Unapproved':
          setPendingAuthorizationId(discipline.authorizationId);
          request.mutate(
            {
              action: 'Approve',
              authorizationId: discipline.authorizationId,
              approvalId: discipline.approvalId,
              caseId: discipline.caseId,
              discipline: discipline.name,
              trainingCenterName: trainingCenterName,
            },
            makeCallbacks('Approve')
          );
          break;

        case 'Approved':
          setPendingAuthorizationId(discipline.authorizationId);
          request.mutate(
            {
              action: 'Revoke',
              authorizationId: discipline.authorizationId,
              approvalId: discipline.approvalId,
              caseId: discipline.caseId,
              discipline: discipline.name,
              trainingCenterName: trainingCenterName,
              description: description || 'Revoke from CAE Connect',
            },
            makeCallbacks('Revoke')
          );
          break;

        case 'Pending':
          setPendingAuthorizationId(discipline.authorizationId);
          request.mutate(
            {
              action: 'Cancel',
              authorizationId: discipline.authorizationId,
              caseId: discipline.caseId,
              approvalId: discipline.approvalId,
              discipline: discipline.name,
              trainingCenterName: trainingCenterName,
            },
            makeCallbacks('CancelApproval')
          );
          break;

        case 'PendingRemoval':
          setPendingAuthorizationId(discipline.authorizationId);
          request.mutate(
            {
              action: 'Cancel',
              authorizationId: discipline.authorizationId,
              caseId: discipline.caseId,
              approvalId: discipline.approvalId,
              discipline: discipline.name,
              trainingCenterName: trainingCenterName,
            },
            makeCallbacks('CancelRevocation')
          );
          break;

        case 'PendingApprovalLetter':
          onUploadLetter(discipline, programId);
          break;

        case 'Unauthorized':
          break;
      }
    },
    [request, trainingCenterName, onUploadLetter, customerId, queryClient]
  );

  return {
    handleDisciplineAction,
    isAnyPending,
    pendingAuthorizationId,
    lastActionResult,
    clearLastActionResult,
  };
}
